import Image from "next/image";
import { IntentPrefetchLink } from "@/components/UI/IntentPrefetchLink";
import { getProductPaymentInfo } from "@/lib/commerce/productPayment";
import { isAllowedProductImageUrl } from "@/lib/media/productImageHosts";
import type { ProductImage } from "@/types/product";
import { ProductCardAction } from "./ProductCardAction";
import { ProductCardActions } from "./ProductCardActions";
import { FreeShippingBadge } from "./FreeShippingBadge";

const PRODUCT_FALLBACK_IMAGE =
  "/images/brand/persi-materiais-eletricos-e-hidraulicos-ferramentas.webp";

function getProductImageSource(image: string): string {
  const normalizedImage = image.trim();
  if (normalizedImage.startsWith("/images/")) return normalizedImage;
  return isAllowedProductImageUrl(normalizedImage) ? normalizedImage : PRODUCT_FALLBACK_IMAGE;
}

function getOptionalProductImageSource(
  image: string | undefined,
): string | undefined {
  const normalizedImage = image?.trim();
  if (!normalizedImage) return undefined;
  if (normalizedImage.startsWith("/images/")) return normalizedImage;
  return isAllowedProductImageUrl(normalizedImage) ? normalizedImage : undefined;
}

function formatCurrency(value: number, currencyCode: string): string {
  try {
    return new Intl.NumberFormat("pt-BR", {
      style: "currency",
      currency: currencyCode,
    }).format(value);
  } catch {
    return new Intl.NumberFormat("pt-BR", {
      style: "currency",
      currency: "BRL",
    }).format(value);
  }
}

export type ProductCardProps = {
  name: string;
  image: string;
  images?: ProductImage[];
  href: string;
  price: number;
  regularPrice?: number;
  currencyCode?: string;
  commercialText?: string;
  brand?: string;
  badge?: string;
  available?: boolean;
  priority?: boolean;
  showAddToCart?: boolean;
  productId?: number;
  productSlug?: string;
  productType?: string;
  isPurchasable?: boolean;
  hasOptions?: boolean;
  showActions?: boolean;
  freeShipping?: boolean;
  /** Card mais baixo (carrosséis da Home): menos respiro e sem altura mínima no preço. */
  compact?: boolean;
};

export function ProductCard({
  name,
  image,
  images,
  href,
  price,
  regularPrice,
  currencyCode = "BRL",
  badge,
  available = true,
  priority = false,
  showAddToCart = false,
  productId,
  productSlug,
  productType = "simple",
  isPurchasable = false,
  hasOptions = false,
  showActions = true,
  freeShipping = false,
  compact = false,
}: ProductCardProps) {
  const imageSrc = getProductImageSource(image);
  const secondaryImage = images?.[1];
  const secondaryImageSrc =
    secondaryImage?.src.trim() !== image.trim()
      ? getOptionalProductImageSource(secondaryImage?.src)
      : undefined;
  const hasDiscount =
    regularPrice !== undefined && regularPrice > price;
  // Percentual sobre o preço normal, arredondado como nas Ofertas Relâmpago
  // (FlashDealCard); abaixo de 1% não mostra.
  const discountPercent =
    hasDiscount && regularPrice ? Math.round((1 - price / regularPrice) * 100) : 0;
  const rightBadges = (discountPercent > 0 ? 1 : 0) + (available ? 0 : 1);
  const payment = getProductPaymentInfo({
    currentPrice: price,
    isVariable: productType === "variable",
  });

  return (
    <article className="group relative flex h-full flex-col overflow-hidden rounded-xl border border-slate-200 bg-white transition duration-200 hover:-translate-y-1 hover:shadow-md active:translate-y-0 active:shadow-sm">
      <IntentPrefetchLink
        href={href}
        data-route-transition-skip
        className={`product-card-image-link relative block ${compact ? "aspect-[5/4]" : "aspect-square"} overflow-hidden bg-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-inset`}
      >
        {badge ? (
          <span className="absolute left-2 top-2 z-10 rounded-md bg-secondary px-2 py-1 text-xs font-semibold text-white">
            {badge}
          </span>
        ) : null}
        {freeShipping ? (
          <span className="absolute bottom-2 left-2 z-10"><FreeShippingBadge compact /></span>
        ) : null}

        {/* Canto direito: o desconto em cima; "Em falta" (e o coração, em
            ProductCardActions) descem logo abaixo. */}
        {rightBadges > 0 ? (
          <span className="absolute right-2 top-2 z-10 flex flex-col items-end gap-1">
            {discountPercent > 0 ? (
              <span className="rounded-md bg-emerald-700 px-2 py-1 text-xs font-bold text-white" aria-label={`${discountPercent}% de desconto`}>
                -{discountPercent}%
              </span>
            ) : null}
            {!available ? (
              <span className="rounded-md bg-slate-700 px-2 py-1 text-xs font-semibold text-white">Em falta</span>
            ) : null}
          </span>
        ) : null}

        <Image
          src={imageSrc}
          alt={name}
          width={480}
          height={480}
          sizes="(min-width: 1024px) 25vw, (min-width: 768px) 33vw, 50vw"
          priority={priority}
          className={`product-card-primary-image absolute inset-0 h-full w-full object-contain ${compact ? "p-3" : "p-4"} ${
            secondaryImageSrc ? "product-card-has-secondary-image" : ""
          } ${available ? "" : "product-card-image-unavailable"
          }`}
        />
        {secondaryImageSrc ? (
          <Image
            src={secondaryImageSrc}
            alt=""
            aria-hidden="true"
            width={480}
            height={480}
            sizes="(min-width: 1024px) 25vw, (min-width: 768px) 33vw, 50vw"
            className={`product-card-secondary-image absolute inset-0 h-full w-full object-contain ${compact ? "p-3" : "p-4"} ${
              available ? "" : "product-card-image-unavailable"
            }`}
          />
        ) : null}
      </IntentPrefetchLink>

      {showActions && productId && productSlug ? (
        <ProductCardActions
          topRightBadges={rightBadges}
          productId={productId}
          productSlug={productSlug}
          productName={name}
        />
      ) : null}

      <div className={`flex flex-1 flex-col ${compact ? "px-3 pb-3 pt-2" : "p-4"}`}>
        <IntentPrefetchLink
          href={href}
          
          data-route-transition-skip
          className="rounded-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2"
        >
          <h3 className="line-clamp-2 min-h-9 text-[13px] font-semibold leading-[18px] text-slate-800 transition-colors group-hover:text-secondary md:min-h-10 md:text-sm md:leading-5">
            {name}
          </h3>
        </IntentPrefetchLink>

        <div className={compact ? "mt-auto pt-2" : "mt-auto min-h-32 pt-3"}>
          {hasDiscount ? (
            <p className="text-xs text-muted line-through">
              {formatCurrency(regularPrice, currencyCode)}
            </p>
          ) : (
            compact ? null : <div className="h-4" aria-hidden="true" />
          )}

          <p className="mt-1 text-base font-bold leading-5 text-emerald-700 md:text-lg md:leading-6">
            {formatCurrency(payment.pixPrice, currencyCode)}
            <span className="ml-1 whitespace-nowrap text-[11px] font-semibold md:text-xs">
              no Pix
            </span>
          </p>

          <div className="mt-1.5 text-[11px] font-medium leading-[15px] text-muted md:text-xs md:leading-4">
            {payment.installments === 1 ? (
              <p>
                ou {formatCurrency(payment.currentPrice, currencyCode)} sem
                juros no cartão
              </p>
            ) : (
              <p>
                ou {formatCurrency(payment.currentPrice, currencyCode)} em até{" "}
                {payment.installments}x de{" "}
                {formatCurrency(payment.installmentValue, currencyCode)} sem
                juros no cartão
              </p>
            )}
          </div>
          {showAddToCart && productId ? (
            <ProductCardAction
              productId={productId}
              productType={productType}
              href={href}
              available={available}
              isPurchasable={isPurchasable}
              hasOptions={hasOptions}
            />
          ) : null}
        </div>
      </div>
    </article>
  );
}
